/**
 * A small syntax colourer for the lines of a diff (DOM-free). Not a parser: it reads a line at a time with the little
 * state a language needs between lines (an open block comment, an open template string) and cuts it into comments,
 * strings, numbers, keywords and plain text. Good enough to read code by; it never throws on odd input and never
 * changes the text, so joining a line's tokens gives the line back.
 *
 * Added and removed lines come from two files (the new and the old), so a diff keeps one state per side.
 */

export type TokenKind = 'comment' | 'string' | 'number' | 'keyword' | 'type' | 'punct';
export interface Token {
  text: string;
  kind?: TokenKind;
}

type Family = 'c' | 'hash' | 'markup' | 'json' | 'sql' | 'plain';

const EXT: Record<string, Family> = {
  ts: 'c', tsx: 'c', js: 'c', jsx: 'c', mjs: 'c', cjs: 'c', java: 'c', go: 'c', rs: 'c', c: 'c', h: 'c', cpp: 'c', hpp: 'c', cs: 'c', swift: 'c', kt: 'c', php: 'c', scala: 'c', dart: 'c', css: 'c', scss: 'c', less: 'c',
  py: 'hash', sh: 'hash', bash: 'hash', zsh: 'hash', ps1: 'hash', rb: 'hash', yml: 'hash', yaml: 'hash', toml: 'hash', ini: 'hash', env: 'hash', dockerfile: 'hash', r: 'hash',
  html: 'markup', htm: 'markup', xml: 'markup', svg: 'markup', vue: 'markup', md: 'markup', mdx: 'markup',
  json: 'json', jsonc: 'json', json5: 'json',
  sql: 'sql',
};

/** The family a file's lines are read as, from its name. */
export function familyOf(path: string): Family {
  const base = path.split('/').pop() ?? path;
  if (base.toLowerCase() === 'dockerfile' || base.toLowerCase() === 'makefile') return 'hash';
  const dot = base.lastIndexOf('.');
  return (dot >= 0 ? EXT[base.slice(dot + 1).toLowerCase()] : undefined) ?? 'plain';
}

const KEYWORDS = new Set(
  (
    'abstract as async await break case catch class const continue debugger default delete do else enum export extends false finally for from function get if implements import in instanceof interface let new null of override package private protected public readonly return set static super switch this throw true try type typeof undefined var void while with yield ' +
    'fn impl mod pub use mut match loop where trait struct self Self crate unsafe dyn ref move ' +
    'def elif except lambda None True False pass raise global nonlocal is not and or print async with del assert ' +
    'func go chan defer select range map fallthrough ' +
    'fi then done esac echo local export function set unset'
  ).split(/\s+/),
);
const SQL_KEYWORDS = new Set('select from where insert into update delete create table alter drop index join left right inner outer on group by order having limit offset values set and or not null as distinct union primary key foreign references default'.split(' '));

interface State {
  /** Inside a /* … *\/ (or <!-- … -->) comment. */
  block: boolean;
  /** Inside a `…` template string. */
  template: boolean;
}

export function freshState(): State {
  return { block: false, template: false };
}

function push(out: Token[], text: string, kind?: TokenKind): void {
  if (!text) return;
  const last = out[out.length - 1];
  if (last && last.kind === kind) last.text += text;
  else out.push(kind ? { text, kind } : { text });
}

/** Cut one line into tokens, carrying `state` to the next line of the same file. */
export function tokenizeLine(line: string, family: Family, state: State): Token[] {
  const out: Token[] = [];
  if (family === 'plain') return line ? [{ text: line }] : [];
  let i = 0;
  const n = line.length;
  const close = family === 'markup' ? '-->' : '*/';
  while (i < n) {
    if (state.block) {
      const end = line.indexOf(close, i);
      if (end < 0) {
        push(out, line.slice(i), 'comment');
        return out;
      }
      push(out, line.slice(i, end + close.length), 'comment');
      i = end + close.length;
      state.block = false;
      continue;
    }
    if (state.template) {
      let j = i;
      while (j < n && line[j] !== '`') j += line[j] === '\\' ? 2 : 1;
      if (j >= n) {
        push(out, line.slice(i), 'string');
        return out;
      }
      push(out, line.slice(i, j + 1), 'string');
      i = j + 1;
      state.template = false;
      continue;
    }
    const c = line[i];
    const two = line.slice(i, i + 2);
    // Comments.
    if (family === 'c' || family === 'sql') {
      if (two === '//' && family === 'c') {
        push(out, line.slice(i), 'comment');
        return out;
      }
      if (two === '--' && family === 'sql') {
        push(out, line.slice(i), 'comment');
        return out;
      }
      if (two === '/*') {
        state.block = true;
        push(out, '/*', 'comment');
        i += 2;
        continue;
      }
    }
    if (family === 'hash' && c === '#') {
      push(out, line.slice(i), 'comment');
      return out;
    }
    if (family === 'markup' && line.startsWith('<!--', i)) {
      state.block = true;
      push(out, '<!--', 'comment');
      i += 4;
      continue;
    }
    // Strings.
    if (c === '"' || c === "'" || (c === '`' && family === 'c')) {
      if (c === '`') {
        state.template = true;
        push(out, '`', 'string');
        i++;
        continue;
      }
      let j = i + 1;
      while (j < n && line[j] !== c) j += line[j] === '\\' ? 2 : 1;
      // A JSON key reads as a name, not a value.
      const isKey = family === 'json' && /^\s*:/.test(line.slice(Math.min(j + 1, n)));
      push(out, line.slice(i, Math.min(j + 1, n)), isKey ? 'type' : 'string');
      i = Math.min(j + 1, n);
      continue;
    }
    // Numbers.
    if (/[0-9]/.test(c) && !/[A-Za-z_$]/.test(line[i - 1] ?? '')) {
      let j = i + 1;
      while (j < n && /[0-9a-fA-FxX_.eE]/.test(line[j])) j++;
      push(out, line.slice(i, j), 'number');
      i = j;
      continue;
    }
    // Words.
    if (/[A-Za-z_$]/.test(c)) {
      let j = i + 1;
      while (j < n && /[A-Za-z0-9_$]/.test(line[j])) j++;
      const word = line.slice(i, j);
      if (family === 'markup') push(out, word);
      else if (family === 'sql') push(out, word, SQL_KEYWORDS.has(word.toLowerCase()) ? 'keyword' : undefined);
      else if (family === 'json') push(out, word, word === 'true' || word === 'false' || word === 'null' ? 'keyword' : undefined);
      else if (KEYWORDS.has(word)) push(out, word, 'keyword');
      else if (/^[A-Z][A-Za-z0-9]*$/.test(word) && word.length > 1) push(out, word, 'type');
      else push(out, word);
      i = j;
      continue;
    }
    if (family === 'markup' && c === '<') {
      const m = /^<\/?[A-Za-z][\w:-]*/.exec(line.slice(i));
      if (m) {
        push(out, m[0], 'keyword');
        i += m[0].length;
        continue;
      }
    }
    push(out, c);
    i++;
  }
  return out;
}

export interface RowLike {
  kind: 'add' | 'del' | 'context' | 'hunk' | 'meta';
  text: string;
  path?: string;
}

/**
 * Tokens for every row of a numbered diff: context lines advance both files' states, an added line the new file's,
 * a removed line the old file's. Rows that are not code (hunk and header lines) come back as `undefined`. A row's own
 * `path` (a diff of several files) wins over `path`, and a new file starts with fresh states.
 */
export function tokenizeRows(rows: readonly RowLike[], path: string): Array<Token[] | undefined> {
  let next = freshState();
  let prev = freshState();
  let at: string | undefined;
  return rows.map((r) => {
    const file = r.path ?? path;
    if (file !== at) {
      at = file;
      next = freshState();
      prev = freshState();
    }
    const family = familyOf(file);
    if (family === 'plain') return undefined;
    if (r.kind === 'add') return tokenizeLine(r.text, family, next);
    if (r.kind === 'del') return tokenizeLine(r.text, family, prev);
    if (r.kind === 'context') {
      const t = tokenizeLine(r.text, family, next);
      // Keep the old side's state in step without keeping its tokens.
      tokenizeLine(r.text, family, prev);
      return t;
    }
    return undefined;
  });
}
